import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { inject, Injectable } from '@angular/core';
import { ActivationStart, Params, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { FeatureFlag } from './feature-flag';
import { MmuFeatureFlagsDefinition } from './feature-flags-definition';
import { MMU_FEATURE_FLAG_PREFIX } from './feature-flags-query-param-prefix';


@Injectable({
  providedIn: 'root'
})
export class MmuFeatureFlagsService {

  private readonly _metaFlags: MmuFeatureFlagsDefinition;
  private readonly _cookieFlags: MmuFeatureFlagsDefinition;
  private _queryParamsFlags: MmuFeatureFlagsDefinition = {};
  private readonly _router = inject(Router);
  private readonly _featureFlagPrefix: string = inject(MMU_FEATURE_FLAG_PREFIX);

  get flags(): MmuFeatureFlagsDefinition {
    return { ...this._metaFlags, ...this._cookieFlags, ...this._queryParamsFlags };
  }

  constructor() {
    this._metaFlags = this.readFeatureFlagsFromMetaTags();
    this._cookieFlags = this.readFeatureFlagsFromCookies();
    this.registerRouterEventListener();
  }

  /**
   * Check if a feature by a given name is enabled or disabled. Returns true when the feature is enabled.
   *
   * @param feature The feature's name
   */
  isFeatureEnabled(feature: FeatureFlag): boolean {
    return Object.prototype.hasOwnProperty.call(this.flags, feature) && this.flags[feature];
  }

  /**
   * Read the meta tags in the DOM to find features flags that have been added there and update the internal flags object.
   * Returns the feature flags from the meta tags only.
   */
  readFeatureFlagsFromMetaTags(): MmuFeatureFlagsDefinition {
    const elements = document.querySelectorAll<HTMLMetaElement>('meta[content=feature]');
    const metaFeatureFlags: Record<string, boolean> = {};

    for (const element of elements) {
      const featureName = (element as HTMLMetaElement).getAttribute('name');
      const enabled = (element as HTMLMetaElement).getAttribute('enabled');

      if (featureName) {
        metaFeatureFlags[featureName] = coerceBooleanProperty(enabled);
      }
    }

    return metaFeatureFlags;
  }

  readFeatureFlagsFromCookies(): MmuFeatureFlagsDefinition {
    const allCookies = document.cookie.split('; ');
    const cookieFeatureFlags: Record<string, boolean> = {};

    const featureFlagCookies = allCookies.filter((cookie) => (cookie.startsWith(this._featureFlagPrefix) ? cookie : null));

    for (const cookie of featureFlagCookies) {
      const featureName = cookie.split('=')[0].substring(this._featureFlagPrefix.length);
      const enabled = cookie.split('=')[1];

      if (featureName) {
        cookieFeatureFlags[featureName] = coerceBooleanProperty(enabled);
      }
    }

    return cookieFeatureFlags;
  }

  /**
   * Provide additional Feature Flags as params before registerRouteParamListener() is executed.
   * Can be used in RouteGuard before the actual route is activated.
   *
   * @param params The Router params object
   */
  setFeatureFlagsByParams(params: Params): void {
    const flagsDefinitions = this.mapQueryParamToFlags(params);
    this._queryParamsFlags = { ...flagsDefinitions };
  }

  private registerRouterEventListener(): void {
    this._router.events.pipe(
      filter((events): events is ActivationStart => events instanceof ActivationStart),
      map((activationStartEvent: ActivationStart) => {
        return this.mapQueryParamToFlags(activationStartEvent.snapshot.queryParams);
      })
    ).subscribe((queryFeatures) => {
      this._queryParamsFlags = { ...queryFeatures };
    });
  }

  /**
   * Map an Angular Router Params object to an MmuFeatureFlagDefinition.
   * Features that should be considered here must be prefixed with the value defined in this._featureFlagPrefix, which defaults
   * to 'feature_'. Therefore, a URL can look like this: 'https://www.my-app.com/some-page?feature_myFeature=true'
   *
   * @param params The Router params object
   */
  private mapQueryParamToFlags = (params: Params): MmuFeatureFlagsDefinition => {
    const keys = Object.keys(params);
    const res: Record<string, boolean> = {};

    for (const key of keys) {
      if (key.indexOf(this._featureFlagPrefix) === 0) {
        const featureName = key.substring(this._featureFlagPrefix.length);
        res[featureName] = coerceBooleanProperty(params[key]);
      }
    }

    return res;
  };
}
